import { ContactCompanyType } from 'models/contact_companies'
import { Preferences } from 'models/preferences'
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import useApiService from 'services/api'
import { getToken } from 'utils/http-client'

const PreferencesContext = createContext<{
  preferences: Preferences | undefined
  isLoading: boolean
  isSettingEnabled: (key: string) => boolean
}>({
  preferences: undefined,
  isLoading: true,
  isSettingEnabled: () => false,
})

export const PreferencesProvider = ({ children }: { children: ReactNode }) => {
  const hasToken = !!getToken()
  const api = useApiService()
  const [isLoading, setIsLoading] = useState(true)

  const initialPreferences = useMemo(
    () => ({
      tooltips: {},
      feature_flags: {},
      misc_settings: [],
      company_type_prefix_map: JSON.parse(
        localStorage.getItem('company_type_prefix_map') ||
          JSON.stringify({
            [ContactCompanyType.Chain]: 'RE',
            [ContactCompanyType.University]: 'CU',
            [ContactCompanyType.K12District]: 'DI',
            [ContactCompanyType.UserGenerated]: 'UG',
            [ContactCompanyType.Hospital]: 'HO',
            [ContactCompanyType.NursingHome]: 'NH',
          })
      ),
      reporting_unit: 'lbs',
    }),
    []
  )

  const [localPreferences, setLocalPreferences] =
    useState<Preferences>(initialPreferences)

  const { data: serverPreferences, isLoading: isQueryLoading } =
    api.useGetPreferences(!hasToken)

  useEffect(() => {
    if (serverPreferences) {
      setLocalPreferences((prevPreferences) => {
        const server_company_type_prefix_map_json = JSON.stringify(
          serverPreferences.company_type_prefix_map
        )
        if (
          JSON.stringify(prevPreferences.company_type_prefix_map) !==
          server_company_type_prefix_map_json
        ) {
          localStorage.setItem(
            'company_type_prefix_map',
            server_company_type_prefix_map_json
          )
        }
        return serverPreferences
      })
      setIsLoading(false)
    }
  }, [serverPreferences])

  // Keep loading true if we have server data but haven't updated local preferences yet
  const preferencesNotYetSynced =
    !!serverPreferences &&
    localPreferences.reporting_unit !== serverPreferences.reporting_unit
  const actualIsLoading = isQueryLoading || isLoading || preferencesNotYetSynced

  const isSettingEnabled = (key: string): boolean => {
    return (
      localPreferences?.misc_settings?.find((s) => s.key === key)?.value ===
      'true'
    )
  }

  return (
    <PreferencesContext.Provider
      value={{
        preferences: localPreferences,
        isLoading: actualIsLoading,
        isSettingEnabled,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  )
}

export const usePreferences = () => {
  const context = useContext(PreferencesContext)
  return context
}
